// AFE taxonomy — the Builder's PHASE → CATEGORY → LINE tree.
//
// THE DATA LIVES IN `afeTaxonomy.json`. Edit that file to change the tree — add a phase, move a
// line, reword a hint. It is a plain JSON document precisely so the taxonomy can evolve with domain
// knowledge without anyone editing TypeScript (PO 2026-08-24). This module owns the SHAPE, the
// validation and the resolution rules; it owns none of the content.
//
// Three levels:
//   PHASE      Pre-Drilling, Drilling, Casing & Tubulars, …  — the only thing visible collapsed
//   CATEGORY   Rig Rate, Services, Bit & BHA, …              — only where a phase needs the split
//   LINE       the actual AfeLine (Group / Comment / Units / Cost per unit)
//
// A phase may hold lines DIRECTLY (Pre-Drilling), or alongside its categories (Drilling's drill-pipe
// line), so the tree is only as deep as the work requires.
//
// PER-SECTION nodes. A phase or category marked `perSection` is not a fixed node: it expands into
// one subgroup PER HOLE SECTION, each seeded with `sectionDefaults`. Casing, Cementing, Mud Products
// and Bit & BHA work this way because that work is bought per string, not per well. A per-section
// PHASE may also declare `defaults`: lines bought once per well (Casing's liner), held in a
// self-leaf ahead of the sections.
//
// VALIDATED AT LOAD. The JSON is hand-edited, so it is checked once at module init: ids unique and
// non-empty, every node reachable, no node both per-section and category-bearing, every default line
// named. A malformed edit fails loudly at startup rather than rendering a silently broken tree.
//
// PROVENANCE. Nodes and lines the PO dictated carry `"po": true`. The rest were extrapolated from
// the two reference workbooks (docs/DESIGN_afe_worksheet_reference.md) and are pending review —
// `grep -L '"po": true'` finds them. No figures live here: labels and units only.

import rawTaxonomy from "./afeTaxonomy.json";

/** A default line the skeleton seeds. No price: an AFE is authored, not guessed. */
export interface AfeLineTemplate {
  description: string;
  /** Suggested UoM ("day", "ea", "ft", "ls", "bbl"). The engineer may change it. */
  unit?: string;
  /** True when the PO named this line explicitly. Absent = extrapolated, pending review. */
  po?: boolean;
  /** What the line covers, shown as guidance on a suggestion (#69). Not a separate line. */
  hint?: string;
}

export interface AfeCategory {
  id: string;
  label: string;
  icon?: string;
  hint?: string;
  /** PER-SECTION category — one subgroup per hole section, seeded with `sectionDefaults`. */
  perSection?: boolean;
  sectionDefaults?: AfeLineTemplate[];
  defaults: AfeLineTemplate[];
  po?: boolean;
}

export interface AfePhase {
  id: string;
  label: string;
  icon?: string;
  hint?: string;
  /** PER-SECTION phase — its categories ARE the hole sections. Its `defaults`, if any, are
   *  once-per-well lines in a self-leaf ahead of the sections. */
  perSection?: boolean;
  sectionDefaults?: AfeLineTemplate[];
  /** Sub-split, when the phase needs one. */
  categories?: AfeCategory[];
  /** Lines held by the phase itself — rendered as rows, not a subgroup. */
  defaults?: AfeLineTemplate[];
  po?: boolean;
}

interface RawTaxonomy {
  version: number;
  /** The drilling tree. Kept as the top-level default so every existing reader is unchanged. */
  phases: AfePhase[];
  /** Per-AFE-class trees. A class absent here falls back to `phases`. */
  trees?: Record<string, AfePhase[]>;
  /** Add-on trees shown on EVERY AFE below its class tree (#69): Geothermal, Subsea. */
  verticalTrees?: AfeVerticalTree[];
}

/** An add-on tree (#69). Every AFE shows it below its class tree, collapsed, whatever its class or
 *  well type (PO 2026-10-13). Its lines are SUGGESTIONS: none is seeded into a new AFE, and a
 *  suggestion becomes a real line only when the user adds it. Holds only what is specific to the
 *  vertical; anything rigs use generally lives in the standard tree. No Miscellaneous phase of its
 *  own: misfits use the class tree's. */
export interface AfeVerticalTree {
  id: string;
  label: string;
  icon?: string;
  phases: AfePhase[];
}

/** Fail loudly on a malformed hand-edit rather than rendering a silently broken tree. */
function validate(phases: AfePhase[], treeName: string): AfePhase[] {
  const where = `afeTaxonomy.json (${treeName})`;
  if (!Array.isArray(phases) || phases.length === 0) {
    throw new Error(`${where}: must be a non-empty array of phases`);
  }
  // Ids are unique WITHIN a tree, not across trees: the abandonment tree is free to reuse a
  // label-level concept without colliding with drilling's node of the same idea.
  const seen = new Set<string>();
  const claim = (id: string, what: string): void => {
    if (!id || typeof id !== "string") {
      throw new Error(`${where}: ${what} has a missing or non-string id`);
    }
    if (seen.has(id)) {
      // A duplicate id silently merges two nodes and misfiles every line in one of them.
      throw new Error(`${where}: duplicate id "${id}" (${what})`);
    }
    seen.add(id);
  };
  const checkLines = (
    lines: AfeLineTemplate[] | undefined,
    what: string,
  ): void => {
    for (const [i, l] of (lines ?? []).entries()) {
      if (!l?.description?.trim()) {
        throw new Error(`${where}: ${what} line ${i} has no description`);
      }
    }
  };

  for (const p of phases) {
    claim(p.id, `phase "${p.label ?? p.id}"`);
    if (!p.label?.trim())
      throw new Error(`${where}: phase "${p.id}" has no label`);
    if (p.perSection && p.categories) {
      // Its categories ARE the sections; declaring both means one silently wins.
      throw new Error(
        `${where}: phase "${p.id}" is perSection AND declares categories — pick one`,
      );
    }
    if (p.perSection && !p.sectionDefaults?.length) {
      throw new Error(
        `${where}: perSection phase "${p.id}" has no sectionDefaults`,
      );
    }
    checkLines(p.defaults, `phase "${p.id}"`);
    checkLines(p.sectionDefaults, `phase "${p.id}" sectionDefaults`);

    for (const c of p.categories ?? []) {
      claim(c.id, `category "${c.label ?? c.id}" in phase "${p.id}"`);
      if (!c.label?.trim())
        throw new Error(`${where}: category "${c.id}" has no label`);
      if (c.perSection && !c.sectionDefaults?.length) {
        throw new Error(
          `${where}: perSection category "${c.id}" has no sectionDefaults`,
        );
      }
      checkLines(c.defaults, `category "${c.id}"`);
      checkLines(c.sectionDefaults, `category "${c.id}" sectionDefaults`);
    }
  }
  return phases;
}

const RAW = rawTaxonomy as unknown as RawTaxonomy;

/** The DRILLING taxonomy, in chronological phase order. Unchanged: every existing reader that
 *  imports `AFE_PHASES` keeps getting exactly this. */
export const AFE_PHASES: readonly AfePhase[] = validate(RAW.phases, "phases");

/** Per-class trees, validated at load like the default one. Added 2026-08-31 for the abandonment
 *  class (`afeClassOptions.ts`), whose work is a different shape from drilling — no hole sections,
 *  no mud program, and a Downhole Tools group carrying plugs and working tools.
 *
 *  PO 2026-08-31, on why plugging is NOT modelled per-plug or per-regulatory-requirement:
 *  "An AFE doesnt care about the state requirements. We may well exceed them. we just need a
 *  section for downhole tools that has plugs and working tools like mills etc."
 *  The regulatory plug program lives in the abandonment program (RM-19b); this prices what is
 *  bought. Recorded in docs/DECISIONS.md 2026-08-31. */
const CLASS_TREES: Readonly<Record<string, readonly AfePhase[]>> =
  Object.freeze(
    Object.fromEntries(
      Object.entries(RAW.trees ?? {}).map(([cls, phases]) => [
        cls,
        validate(phases, `trees.${cls}`),
      ]),
    ),
  );

/** The phase tree for an AFE class. A class with no tree of its own falls back to drilling —
 *  deliberate: a new class gets a working Builder before its taxonomy is authored, rather than an
 *  empty one.
 *
 *  ── ADDING AN AFE CLASS: what it must ship with (PO 2026-08-31) ──────────────────────────────
 *  A class is not "done" when its tree exists. Every class the Builder can render must also have:
 *
 *    1. ICONS on every phase and sub-group. `SectionIcon` falls back to a neutral dot for a
 *       missing or unknown name, so an omission is invisible — asserted in
 *       `afeTaxonomyAbandonment.test.ts`, which also checks each name resolves in the registry.
 *    2. QUICK FIND. Structural today (one `AfeLineGrid`, not gated on class), and locked in by
 *       `AfeLineGridFindParity.test.tsx`, which drives itself off `afeClassesWithTree()` — a new
 *       class is covered the day it lands, without anyone remembering to write the test.
 *    3. A MISCELLANEOUS PHASE. `migrateAfeBudgetClass` files misfits there ("misc is the place for
 *       misfits"), and `miscLeafId` must know its id.
 *    4. CLASS-AWARE SEEDING. `afeLeaves`/`skeletonLines` take the class; every seed path passes
 *       it. Miss one and the AFE is born with another class's lines — which shipped once, and
 *       rendered as phase headers with nothing under them.
 *    5. The picker entry ENABLED only once the tree exists — enabled-without-a-tree silently
 *       renders the drilling taxonomy under the new class's heading. Asserted in
 *       `AfeStartPage.test.tsx`. */
/** The add-on trees, in display order, validated at load. Their ids must not collide with any
 *  class tree's: a line's `group` names exactly one leaf across everything an AFE can show. */
export const AFE_VERTICAL_TREES: readonly AfeVerticalTree[] = Object.freeze(
  (RAW.verticalTrees ?? []).map((t) => {
    if (!t.id || !t.label?.trim()) {
      throw new Error(
        "afeTaxonomy.json (verticalTrees): a tree has no id or label",
      );
    }
    validate(t.phases, `verticalTrees.${t.id}`);
    return t;
  }),
);

{
  // Load-time collision check across class trees and add-on trees.
  const classIds = new Set<string>();
  for (const phases of [AFE_PHASES, ...Object.values(CLASS_TREES)]) {
    for (const p of phases) {
      classIds.add(p.id);
      for (const c of p.categories ?? []) classIds.add(c.id);
    }
  }
  const seenVertical = new Set<string>();
  const treeIds = new Set<string>();
  for (const t of AFE_VERTICAL_TREES) {
    // A tree id keys the grid's open state and React rows, so two trees may not share one.
    if (treeIds.has(t.id)) {
      throw new Error(
        `afeTaxonomy.json (verticalTrees): duplicate tree id "${t.id}"`,
      );
    }
    treeIds.add(t.id);
    for (const p of t.phases) {
      for (const id of [p.id, ...(p.categories ?? []).map((c) => c.id)]) {
        if (classIds.has(id) || seenVertical.has(id)) {
          throw new Error(
            `afeTaxonomy.json (verticalTrees.${t.id}): id "${id}" collides with another tree`,
          );
        }
        seenVertical.add(id);
      }
    }
  }
}

export function afePhasesForClass(
  afeClass: string | undefined,
): readonly AfePhase[] {
  return (afeClass && CLASS_TREES[afeClass]) || AFE_PHASES;
}

/** Which classes have a tree of their own (diagnostics / tests). */
export function afeClassesWithTree(): readonly string[] {
  return Object.keys(CLASS_TREES);
}

// ── Sections + leaf resolution ────────────────────────────────────────────────

import type { AfeSection } from "./types";

/** The starting section program when an AFE has none. A conventional four-string well — the wizard
 *  replaces this outright (new) or with a cloned program (clone). Deliberately NOT a guess at THIS
 *  well: it is a starting point the engineer edits, never a claim about the design. */
export const DEFAULT_AFE_SECTIONS: readonly AfeSection[] = [
  { id: "sec-conductor", name: "Conductor", order: 1 },
  { id: "sec-surface", name: "Surface", order: 2 },
  { id: "sec-intermediate", name: "Intermediate", order: 3 },
  { id: "sec-production", name: "Production", order: 4 },
];

/** Join a per-section template to a section. `AfeLine.group` stores this, so a section RENAME never
 *  orphans its lines — only the id matters. */
export function sectionLeafId(templateId: string, sectionId: string): string {
  return `${templateId}::${sectionId}`;
}

/** Split a leaf id back into its template and section, or null for a plain (non-section) leaf. */
export function parseSectionLeafId(
  leafId: string,
): { templateId: string; sectionId: string } | null {
  const at = leafId.indexOf("::");
  if (at === -1) return null;
  return {
    templateId: leafId.slice(0, at),
    sectionId: leafId.slice(at + 2),
  };
}

/** A leaf — the node an AfeLine's `group` points at. */
export interface AfeLeaf {
  id: string;
  label: string;
  hint?: string;
  icon?: string;
  phaseId: string;
  phaseLabel: string;
  defaults: AfeLineTemplate[];
  /** Set when this leaf was generated from a per-section template. */
  sectionId?: string;
  /** The add-on tree this leaf belongs to (#69); absent for the class tree. Such a leaf's defaults
   *  are suggestions, never seeded. */
  vertical?: string;
}

/** Every leaf, expanding per-section templates against `sections`. A phase or category marked
 *  `perSection` contributes ONE leaf per section instead of a single fixed leaf. */
export function afeLeaves(
  sections: readonly AfeSection[] = DEFAULT_AFE_SECTIONS,
  afeClass?: string,
): AfeLeaf[] {
  const ordered = [...sections].sort((a, b) => a.order - b.order);
  const out: AfeLeaf[] = [];

  // CLASS-AWARE (2026-08-31). This hardcoded AFE_PHASES, which meant an abandonment AFE rendered
  // its own phase HEADERS (the grid asks `afePhasesForClass`) while its leaves — and the skeleton
  // seeded from them — still came from the DRILLING tree. Every abandonment header therefore had
  // zero leaves and could not be expanded. The tree must be chosen in exactly one place, and this
  // is it: leaves, skeleton and grid all resolve through the same call.
  const trees: { phases: readonly AfePhase[]; vertical?: string }[] = [
    { phases: afePhasesForClass(afeClass) },
    ...AFE_VERTICAL_TREES.map((t) => ({ phases: t.phases, vertical: t.id })),
  ];
  for (const { phases, vertical } of trees)
    for (const p of phases) {
      const tag = vertical ? { vertical } : {};
      if (p.perSection && p.sectionDefaults) {
        // A per-section phase may also hold lines bought once per WELL, not once per hole section
        // (Liner & Liner Hanger under Casing; docs/DECISIONS.md 2026-10-14). They sit in a
        // self-leaf ahead of the sections, rendered as plain rows under the phase header like
        // Drilling's own lines.
        if (p.defaults?.length) {
          out.push({
            id: p.id,
            label: p.label,
            phaseId: p.id,
            phaseLabel: p.label,
            defaults: p.defaults,
            ...(p.hint ? { hint: p.hint } : {}),
            ...(p.icon ? { icon: p.icon } : {}),
            ...tag,
          });
        }
        for (const sec of ordered) {
          out.push({
            id: sectionLeafId(p.id, sec.id),
            label: sec.name,
            phaseId: p.id,
            phaseLabel: p.label,
            defaults: p.sectionDefaults,
            sectionId: sec.id,
            ...(p.icon ? { icon: p.icon } : {}),
            ...tag,
          });
        }
        continue;
      }
      if (p.categories) {
        // A phase may hold its OWN lines alongside its categories. That self-leaf carries the phase
        // id, and the grid renders its rows directly under the phase header — a line, not a subgroup.
        if (p.defaults?.length) {
          out.push({
            id: p.id,
            label: p.label,
            phaseId: p.id,
            phaseLabel: p.label,
            defaults: p.defaults,
            ...(p.hint ? { hint: p.hint } : {}),
            ...(p.icon ? { icon: p.icon } : {}),
            ...tag,
          });
        }
        for (const c of p.categories) {
          if (c.perSection && c.sectionDefaults) {
            for (const sec of ordered) {
              out.push({
                id: sectionLeafId(c.id, sec.id),
                label: `${c.label} — ${sec.name}`,
                phaseId: p.id,
                phaseLabel: p.label,
                defaults: c.sectionDefaults,
                sectionId: sec.id,
                ...(c.icon ? { icon: c.icon } : {}),
                ...tag,
              });
            }
            continue;
          }
          out.push({
            id: c.id,
            label: c.label,
            phaseId: p.id,
            phaseLabel: p.label,
            defaults: c.defaults,
            ...(c.hint ? { hint: c.hint } : {}),
            ...(c.icon ? { icon: c.icon } : {}),
            ...tag,
          });
        }
        continue;
      }
      out.push({
        id: p.id,
        label: p.label,
        phaseId: p.id,
        phaseLabel: p.label,
        defaults: p.defaults ?? [],
        ...(p.hint ? { hint: p.hint } : {}),
        ...(p.icon ? { icon: p.icon } : {}),
        ...tag,
      });
    }
  return out;
}

/** Sentinel for lines with no group. Rendered, never silently dropped. */
export const UNGROUPED_ID = "";
export const UNGROUPED_LABEL = "Ungrouped";

/** The PHASE a leaf belongs to, resolved WITHOUT a section list: a section leaf carries its
 *  template id, and the template's phase is fixed in the taxonomy. Used on the roll-up wire, where
 *  the FE must not have to thread the section program through just to tag a line. */
export function phaseIdForLeaf(leafId: string | undefined): string | undefined {
  if (!leafId) return undefined;
  const parsed = parseSectionLeafId(leafId);
  const key = parsed?.templateId ?? leafId;
  for (const p of [
    ...AFE_PHASES,
    ...AFE_VERTICAL_TREES.flatMap((t) => t.phases),
  ]) {
    if (p.id === key) return p.id;
    for (const c of p.categories ?? []) if (c.id === key) return p.id;
  }
  return undefined;
}

export function afeLeafLabel(
  id: string | undefined,
  sections?: readonly AfeSection[],
): string {
  if (!id) return UNGROUPED_LABEL;
  return afeLeaves(sections).find((l) => l.id === id)?.label ?? id;
}

/** The full starting skeleton for a section program, in tree order. */
export function skeletonLines(
  sections: readonly AfeSection[] = DEFAULT_AFE_SECTIONS,
  afeClass?: string,
): { group: string; description: string; itemUnit?: string }[] {
  // Add-on trees are never seeded: their lines are suggestions the user adds one at a time (#69).
  return afeLeaves(sections, afeClass)
    .filter((leaf) => !leaf.vertical)
    .flatMap((leaf) =>
      leaf.defaults.map((d) => ({
        group: leaf.id,
        description: d.description,
        ...(d.unit ? { itemUnit: d.unit } : {}),
      })),
    );
}
